/**
 * PlanCockpit — the unified scheduling Plan surface.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │  Toolbar: week nav · utilization · map toggle               │
 *   ├──────────────┬──────────────────────────────────────────────┤
 *   │  Ready rail  │   Week capacity grid (table-fixed)           │
 *   │  (sticky)    │                                              │
 *   ├──────────────┴────────────────┬─────────────────────────────┤
 *   │   Square route map (1:1)      │   Nearest installs (4-10)   │
 *   │                               │   sorted by proximity       │
 *   └───────────────────────────────┴─────────────────────────────┘
 *
 * Cockpit chrome: seamless surface, hairline borders, no card boxes.
 *
 * Keyboard:
 *   ← / →   prev / next week
 *   T       jump to today
 *   M       toggle map
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import { getOnboardingLifecycleRows } from "@/data/contractor/onboarding/lifecycle";
import { useScheduling } from "@/data/scheduling/context";
import { JobService } from "@/services/job.service";
import { JobOperationsService } from "@/services/job-operations.service";
import { buildCapacityGrid, UNASSIGNED_ROW_KEY, type CapacityCell, type CapacityGrid } from "@/data/scheduling/capacityEngine";
import { analyzeDrop, type DropAnalysis } from "@/data/scheduling/dropAnalyzer";
import { getJobRiskSignals } from "@/data/scheduling/riskSignals";
import { buildSchedulingSuggestions, type SchedulingSuggestion } from "@/data/scheduling/suggestions";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";
import { useSchedulingConflicts } from "@/hooks/useSchedulingConflicts";
import { useOrgContractorAvailability } from "@/hooks/useContractorAvailability";
import { ReadyJobBookingDialog } from "./ReadyJobBookingDialog";
import { PlanRouteMap } from "./PlanRouteMap";
import { ConflictResolutionDrawer } from "./ConflictResolutionDrawer";
import { CellPeekPopover } from "./CellPeekPopover";
import { ExtendJobModal } from "./ExtendJobModal";
import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import { SchedulingConfirmInspector, type SchedulingInspectorContext } from "./SchedulingConfirmInspector";
import type { Job } from "@/data/types";
import type { CapacityRow } from "@/data/scheduling/capacityEngine";
import { toast as sonnerToast } from "sonner";
import { assertContractorAssignable } from "@/data/payProfileGuard";
import { assertJobKitReady } from "@/data/kitDispatchGuard";
import { getJobBriefing } from "@/data/contractor/jobBriefing";
import { getPortalSpec } from "@/data/portal/contractorPortalSelectors";
import { getJobPhotoUrls } from "@/services/attachment.service";
import {
  canMarkJobReady,
  isExecutionReady,
  EXECUTION_LOCK_BLOCK_TITLE,
  EXECUTION_LOCK_BLOCK_DESCRIPTION,
  getExecutionLockBlockDescription,
  getExecutionLockToastOptions,
} from "@/data/executionReadyLock";
import { getKit } from "@/data/materialKit";
import {
  assertMaterialAlignment,
  describeAlignmentBlock,
} from "@/data/materialAlignmentGate";
import { useVendorPO } from "@/data/VendorPOContext";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  addDays, parseISO, isValid, startOfWeek, endOfWeek, format, isSameWeek,
} from "date-fns";
import {
  clampInstallShifts,
  createInstallDateTime,
  formatInstallDateIso,
  getInstallCalendarEndForShifts,
  INSTALL_TIME_ZONE,
} from "@/data/scheduling/installSpan";
import {
  Map as MapIcon, ChevronRight, ArrowRight, AlertTriangle,
  ChevronLeft, ChevronsRight, MapPin, GripVertical, Maximize2, Minimize2, X, Undo2,
  UserX,
} from "lucide-react";

// MIME type used by HTML5 DnD payload — kept narrow so unrelated drags are ignored.
const DRAG_MIME = "application/x-koredor-jobid";

function fmtMoney(n: number) {
  if (!n) return "—";
  return n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${n.toLocaleString()}`;
}

function getJobDisplayRef(job: { id: string; jobNumber?: unknown }): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

function isPastInstallDate(dateIso: string): boolean {
  const normalized = String(dateIso || "").trim().slice(0, 10);
  if (!normalized) return false;
  return normalized < format(new Date(), "yyyy-MM-dd");
}

// ── Florida city coordinates (lightweight; avoid bringing in the full geocoder) ──
const CITY_COORDS: Record<string, [number, number]> = {
  "Tampa": [27.9506, -82.4572],
  "St. Petersburg": [27.7676, -82.6403],
  "Clearwater": [27.9659, -82.8001],
  "Sarasota": [27.3364, -82.5307],
  "Bradenton": [27.4989, -82.5748],
  "Fort Myers": [26.6406, -81.8723],
  "Cape Coral": [26.5629, -81.9495],
  "Naples": [26.1420, -81.7948],
  "Lakeland": [28.0395, -81.9498],
  "Brandon": [27.9378, -82.2860],
  "Riverview": [27.8661, -82.3265],
  "Largo": [27.9095, -82.7873],
};

function coordsForCity(city: string): [number, number] | null {
  return CITY_COORDS[city] ?? null;
}

function haversine(a: [number, number], b: [number, number]): number {
  const R = 3958.8; // miles
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function PlanCockpit() {
  const { jobs, setJobs, issues, contractors, crews, opportunities, credits, buildReviewEvents, installerWrapRecords, payProfiles, materialKits } = useAppData();
  const onboarding = useOnboarding();
  const { role } = useRole();
  const canOverrideGating = ["Super Admin", "Admin", "Owner"].includes(role);
  const { purchaseOrders } = useVendorPO();
  const { drafts: orderDrafts } = useOrderDraft();
  const { eventLog, auditTrail } = useScheduling();
  const withdrawnContractorIds = useMemo(
    () =>
      new Set(
        getOnboardingLifecycleRows(onboarding.inviteTokens, onboarding.sessions)
          .filter((row) => row.stage === "withdrawn" && row.contractorId)
          .map((row) => row.contractorId as string),
      ),
    [onboarding.inviteTokens, onboarding.sessions],
  );
  const schedulingContractors = useMemo(
    () => contractors.filter((contractor) => !withdrawnContractorIds.has(contractor.id)),
    [contractors, withdrawnContractorIds],
  );

  // Read initial reference date from URL (?week=YYYY-MM-DD) so bouncing
  // Plan ⇄ Dispatch (or refreshing) keeps you on the same week.
  const [referenceDate, setReferenceDate] = useState<Date>(() => {
    if (typeof window === "undefined") return new Date();
    const sp = new URLSearchParams(window.location.search);
    const w = sp.get("week");
    if (w) {
      const d = parseISO(w);
      if (isValid(d)) return d;
    }
    return new Date();
  });
  const [bookingSlot, setBookingSlot] = useState<Date | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [preselectJobId, setPreselectJobId] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(true);
  const [rangeStart, setRangeStart] = useState<string>(
    format(startOfWeek(referenceDate, { weekStartsOn: 1 }), "yyyy-MM-dd"),
  );
  const [rangeEnd, setRangeEnd] = useState<string>(
    format(endOfWeek(referenceDate, { weekStartsOn: 1 }), "yyyy-MM-dd"),
  );
  const [mapDayFilter, setMapDayFilter] = useState<string | null>(null);
  const [focusJobId, setFocusJobId] = useState<string | null>(null);
  const [planSearch, setPlanSearch] = useState("");
  const [mapFullscreen, setMapFullscreen] = useState(false);
  // Drag state — drives visual affordances on cells while a card is in flight.
  const [draggingJobId, setDraggingJobId] = useState<string | null>(null);
  // True when the dragged card originated from an already-booked cell (cell-to-cell move).
  const [dragOrigin, setDragOrigin] = useState<"rail" | "cell" | null>(null);
  // Currently hovered drop cell + its analysis — drives the inline decision hint.
  const [hoverCell, setHoverCell] = useState<{ crewName: string; dateIso: string } | null>(null);
  // Conflict resolution drawer
  const [conflictJobId, setConflictJobId] = useState<string | null>(null);
  // ── Cell-peek action chain (Extend / Reschedule / Mark complete) ──
  const [extendTarget, setExtendTarget] = useState<{ job: Job; crewRow: CapacityRow | null } | null>(null);
  const [rescheduleJob, setRescheduleJob] = useState<Job | null>(null);
  // While the Extend modal is open this is the candidate shift count;
  // the grid uses it to highlight the would-be span across the crew row.
  const [previewShifts, setPreviewShifts] = useState<number | null>(null);
  // Snapshot of crew row order captured at drag-start so the grid stops
  // reshuffling under the operator's cursor (booking changes utilization,
  // which would otherwise resort the rows mid-drag).
  const [frozenRowOrder, setFrozenRowOrder] = useState<string[] | null>(null);
  // Persisted "booked with warning" justifications, keyed by jobId.
  // The reason is captured at commit time (analyzeDrop) and surfaces on the
  // cell + popover so the operator never loses the "why" once a toast fades.
  const [warningNotes, setWarningNotes] = useState<Map<string, string>>(() => new Map());
  // Confirmation Inspector — opens after meaningful scheduling actions only
  // (drop from rail, conflict resolved, major reschedule). Never on hover/peek.
  const [inspectorCtx, setInspectorCtx] = useState<SchedulingInspectorContext | null>(null);

  const grid = useMemo(
    () => buildCapacityGrid({
      jobs, issues, contractors: schedulingContractors,
      contractorOpportunities: opportunities,
      credits, buildReviewEvents, materialKits, referenceDate,
    }),
    [jobs, issues, schedulingContractors, opportunities, credits, buildReviewEvents, materialKits, referenceDate],
  );

  // Live analysis of the cell currently hovered during a drag.
  const dropAnalysis: DropAnalysis | null = useMemo(() => {
    if (!draggingJobId || !hoverCell) return null;
    return analyzeDrop({
      jobId: draggingJobId,
      targetCrewName: hoverCell.crewName,
      targetDateIso: hoverCell.dateIso,
      grid, jobs, contractors: schedulingContractors,
    });
  }, [draggingJobId, hoverCell, grid, jobs, schedulingContractors]);

  /**
   * Precomputed verdict for EVERY (crew × day) cell, evaluated the moment a
   * drag starts. This lets the grid light up green / amber / red across the
   * whole board so the operator sees viable destinations at a glance — no
   * one-cell-at-a-time hovering. Keyed by `${crewName}|${dateIso}`.
   */
  const dropVerdictMap = useMemo(() => {
    const map = new Map<string, "valid" | "warning" | "blocked">();
    if (!draggingJobId) return map;
    for (const row of grid.rows) {
      for (const cell of row.cells) {
        if (row.crewName === UNASSIGNED_ROW_KEY) {
          map.set(`${row.crewName}|${cell.dateIso}`, "valid");
          continue;
        }
        const a = analyzeDrop({
          jobId: draggingJobId,
          targetCrewName: row.crewName,
          targetDateIso: cell.dateIso,
          grid, jobs, contractors: schedulingContractors,
        });
        map.set(`${row.crewName}|${cell.dateIso}`, a.verdict);
      }
    }
    return map;
  }, [draggingJobId, grid, jobs, schedulingContractors]);

  // Stable row order while a drag is in flight. Using the snapshot prevents
  // the post-drop utilization re-sort from yanking rows out from under the
  // operator's cursor mid-action. Falls back to engine order otherwise.
  const displayRows = useMemo(() => {
    if (!draggingJobId || !frozenRowOrder) return grid.rows;
    const indexOf = (name: string) => {
      const i = frozenRowOrder.indexOf(name);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...grid.rows].sort((a, b) => indexOf(a.crewName) - indexOf(b.crewName));
  }, [grid.rows, draggingJobId, frozenRowOrder]);

  // Drag lifecycle helpers — every drag start snapshots the current row order
  // so the grid is spatially stable until drag ends or undo runs.
  const beginDrag = useCallback((jobId: string, origin: "rail" | "cell") => {
    setDraggingJobId(jobId);
    setDragOrigin(origin);
    setFrozenRowOrder(grid.rows.map((r) => r.crewName));
  }, [grid.rows]);
  const endDrag = useCallback(() => {
    setDraggingJobId(null);
    setDragOrigin(null);
    setHoverCell(null);
    setFrozenRowOrder(null);
  }, []);

  const weekStart = useMemo(() => startOfWeek(referenceDate, { weekStartsOn: 1 }), [referenceDate]);
  const weekEnd = useMemo(() => endOfWeek(referenceDate, { weekStartsOn: 1 }), [referenceDate]);
  const isCurrentWeek = isSameWeek(referenceDate, new Date(), { weekStartsOn: 1 });
  const { availabilityByContractor } = useOrgContractorAvailability(weekStart, weekEnd);
  // companyName → contractorId for O(1) row→availability lookups
  const contractorIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of contractors) map.set(c.companyName, c.id);
    return map;
  }, [contractors]);
  const rangeStartDate = useMemo(() => {
    const d = parseISO(rangeStart);
    return isValid(d) ? d : weekStart;
  }, [rangeStart, weekStart]);
  const rangeEndDate = useMemo(() => {
    const d = parseISO(rangeEnd);
    return isValid(d) ? d : weekEnd;
  }, [rangeEnd, weekEnd]);
  const isInRange = useCallback((isoDate?: string | null) => {
    if (!isoDate) return false;
    const d = parseISO(isoDate);
    if (!isValid(d)) return false;
    return d >= rangeStartDate && d <= rangeEndDate;
  }, [rangeStartDate, rangeEndDate]);

  // Ready rail: split into truly-ready (safe to drop) and borderline almost-ready
  // (visible but visually dimmed and non-draggable). Sorted by revenue × age.
  const { readyJobs, almostReadyJobs } = useMemo(() => {
    const enriched = grid.unscheduled
      .map(u => ({
      ...u,
      revenue: u.job.financials?.contractValue ?? 0,
    }));
    const score = (a: typeof enriched[number]) => (a.revenue / 1000) + a.daysSinceSold * 2;
    const ready = enriched.filter(u => u.readyForInstall).sort((a, b) => score(b) - score(a));
    const almost = enriched.filter(u => !u.readyForInstall).sort((a, b) => score(b) - score(a));
    return {
      readyJobs: ready,
      almostReadyJobs: almost,
    };
  }, [grid.unscheduled]);

  const normalizedPlanSearch = planSearch.trim().toLowerCase();
  const matchesPlanSearch = useCallback((job: Job) => {
    if (!normalizedPlanSearch) return true;
    const ref = getJobDisplayRef(job).toLowerCase();
    const haystack = [
      job.id,
      ref,
      job.customerName,
      job.projectType,
      job.address.city,
      job.assignedCrew ?? "",
      job.assignedSubcontractor ?? "",
    ]
      .map((v) => String(v ?? "").toLowerCase())
      .join(" ");
    return haystack.includes(normalizedPlanSearch);
  }, [normalizedPlanSearch]);

  const filteredReadyJobs = useMemo(
    () => readyJobs.filter((u) => matchesPlanSearch(u.job)),
    [readyJobs, matchesPlanSearch],
  );

  const filteredAlmostReadyJobs = useMemo(
    () => almostReadyJobs.filter((u) => matchesPlanSearch(u.job)),
    [almostReadyJobs, matchesPlanSearch],
  );

  const filteredDisplayRows = useMemo(() => {
    if (!normalizedPlanSearch) return displayRows;
    return displayRows
      .map((row) => {
        const crewMatch = row.crewName.toLowerCase().includes(normalizedPlanSearch);
        const nextCells = row.cells.map((cell) => {
          if (!cell.job) return cell;
          if (crewMatch || matchesPlanSearch(cell.job)) return cell;
          return {
            ...cell,
            state: "open",
            job: null,
            fitScore: null,
            readyForInstall: false,
            blockerLabel: null,
            conflictCount: 0,
            shiftIndex: null,
            totalShifts: null,
            isContinuation: false,
          };
        });
        return { ...row, cells: nextCells };
      })
      .filter((row) => row.cells.some((cell) => cell.job) || row.crewName.toLowerCase().includes(normalizedPlanSearch));
  }, [displayRows, matchesPlanSearch, normalizedPlanSearch]);

  // Canonical gate map used by both rail and booking modal so readiness verdicts stay identical.
  const scheduleReadyByJobId = useMemo(() => {
    const map = new Map<string, { scheduleReady: boolean; blocker: string | null }>();
    for (const u of grid.unscheduled) {
      map.set(u.job.id, {
        scheduleReady: u.readyForInstall,
        blocker: u.topBlocker ?? null,
      });
    }
    return map;
  }, [grid.unscheduled]);

  // Jobs visible on the map: respects active day filter.
  const weekJobs = useMemo(() => {
    const idsByDay = new Map<string, Set<string>>();
    const allIds = new Set<string>();
    for (const row of grid.rows) {
      for (const cell of row.cells) {
        if (cell.job) {
          allIds.add(cell.job.id);
          const set = idsByDay.get(cell.dateIso) ?? new Set<string>();
          set.add(cell.job.id);
          idsByDay.set(cell.dateIso, set);
        }
      }
    }
    const targetIds = mapDayFilter
      ? (idsByDay.get(mapDayFilter) ?? new Set<string>())
      : allIds;
    return jobs.filter(
      j =>
        targetIds.has(j.id) &&
        (!j.scheduledInstallDate || isInRange(j.scheduledInstallDate)) &&
        matchesPlanSearch(j),
    );
  }, [grid.rows, jobs, mapDayFilter, isInRange, matchesPlanSearch]);

  // ── Nearest installs panel: from focused job (or first weekJob if none) ──
  const nearestInstalls = useMemo(() => {
    if (weekJobs.length === 0) return [];
    const anchor = weekJobs.find(j => j.id === focusJobId) ?? weekJobs[0];
    const anchorCoords = coordsForCity(anchor.address.city);
    if (!anchorCoords) {
      return weekJobs.slice(0, 8).map(j => ({ job: j, miles: null as number | null, isAnchor: j.id === anchor.id }));
    }
    return weekJobs
      .map(j => {
        const c = coordsForCity(j.address.city);
        return {
          job: j,
          miles: c ? haversine(anchorCoords, c) : null,
          isAnchor: j.id === anchor.id,
        };
      })
      .sort((a, b) => {
        if (a.isAnchor) return -1;
        if (b.isAnchor) return 1;
        if (a.miles == null) return 1;
        if (b.miles == null) return -1;
        return a.miles - b.miles;
      })
      .slice(0, 10);
  }, [weekJobs, focusJobId]);

  const bookedDays = grid.rows.reduce((s, r) => s + r.bookedDays, 0);
  const capacityDays = grid.rows.reduce((s, r) => s + r.capacityDays, 0);
  const utilization = capacityDays > 0 ? Math.round((bookedDays / capacityDays) * 100) : 0;

  // ── Unscheduled-issue surfacing ────────────────────────────────────
  // Detects conflicts that have no cell to attach to (no_crew, no_schedule).
  // Without this, jobs assigned to a crew but missing a date — or scheduled
  // for a date with no crew — are invisible in the grid and leave silent
  // gaps in the operator's "is everything covered?" mental model.
  const conflictsAll = useSchedulingConflicts(jobs);
  const unscheduledIssueJobs = useMemo(() => {
    const ids = new Set<string>();
    for (const c of conflictsAll.all) {
      if (c.type === "no_crew" || c.type === "no_schedule") ids.add(c.jobId);
    }
    return jobs.filter(j => ids.has(j.id) && (!j.scheduledInstallDate || isInRange(j.scheduledInstallDate)));
  }, [conflictsAll, jobs, isInRange]);

  const openCellBooking = (dateIso: string) => {
    const d = createInstallDateTime(dateIso, 9);
    if (!isValid(d)) return;
    setPreselectJobId(null);
    setBookingSlot(d);
    setBookingOpen(true);
  };

  const openJobBooking = (jobId: string) => {
    const tomorrowIso = formatInstallDateIso(addDays(referenceDate, 1));
    const d = createInstallDateTime(tomorrowIso, 9);
    setPreselectJobId(jobId);
    setBookingSlot(d);
    setBookingOpen(true);
  };

  // ── Drag-and-drop booking ────────────────────────────────────────
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Optimistically book a job onto a (crew, day) cell. Mutates jobs in-place
   * via setJobs; surfaces an 8s "press Z to undo" affordance that restores
   * the prior snapshot. Refuses occupied cells and identical no-op moves.
   */
  const bookJobToCell = useCallback((
    jobId: string,
    crewName: string,
    dateIso: string,
    source: "rail" | "cell" | "conflict" = "cell",
  ) => {
    const target = jobs.find(j => j.id === jobId);
    if (!target) return;

    // ── Unassign path: drop onto the Unassigned row ───────────────
    if (crewName === UNASSIGNED_ROW_KEY) {
      const priorUnassign = {
        scheduledInstallDate: target.scheduledInstallDate,
        assignedCrew: target.assignedCrew,
        crewId: target.crewId,
        assignedSubcontractor: target.assignedSubcontractor,
        assignmentType: target.assignmentType,
      };
      setJobs(prev => prev.map(j => j.id !== jobId ? j : ({
        ...j,
        scheduledInstallDate: dateIso,
        assignedCrew: "",
        crewId: undefined,
        assignedSubcontractor: "",
        assignmentType: undefined,
      })));
      JobService.unassignCrew(jobId, dateIso)
        .then((ok) => {
          if (!ok) {
            setJobs(prev => prev.map(j => j.id !== jobId ? j : ({ ...j, ...priorUnassign })));
            sonnerToast.error("Unassign failed", { description: "Crew assignment was reverted. Please try again." });
          } else {
            sonnerToast.success(`${target.customerName} unassigned · date kept`, { duration: 3000 });
          }
        });
      return;
    }

    // ── No-op guard: drop on origin cell ───────────────────────────
    // Same crew + same date as current assignment = nothing changed.
    // Without this branch the drop appears to "succeed" silently — operator
    // can't tell if the system registered the action.
    const currentAssignee = target.assignmentType === "Subcontractor"
      ? target.assignedSubcontractor
      : target.assignedCrew;
    if (target.scheduledInstallDate === dateIso && currentAssignee === crewName) {
      sonnerToast("No change", {
        description: `${target.customerName} is already on ${crewName} · ${format(parseISO(dateIso), "EEE MMM d")}.`,
        duration: 3000,
      });
      return;
    }

    // ── Commit-time gate re-check ──────────────────────────────────
    // A pre-spend gate may have regressed since drag-start (payment failure,
    // permit revoke, build review reset). Drop UI cannot be trusted to
    // reflect current truth — re-derive here before mutating.
    const gate = derivePreSpendGate(target);
    const bypassNativeGate = isExternalManagedImport(target);
    if (!bypassNativeGate && !gate.cleared && !canOverrideGating) {
      sonnerToast.error(`${target.customerName} can't be scheduled yet`, {
        description: gate.shortReason ?? "Pre-spend gate not cleared.",
        duration: 6000,
      });
      return;
    }
    if (!bypassNativeGate && !gate.cleared && canOverrideGating) {
      sonnerToast.warning("Scheduling override applied", {
        description: gate.shortReason ?? "Pre-spend gate not cleared.",
        duration: 4500,
      });
    }

    // ── Commit-time verdict re-check ───────────────────────────────
    // The dropVerdictMap was computed at drag-start. If utilization/conflict
    // state changed (parallel tab, mid-flight booking), re-run analyzeDrop
    // against the current grid and surface the accurate result.
    const liveAnalysis = analyzeDrop({
      jobId, targetCrewName: crewName, targetDateIso: dateIso, grid, jobs, contractors: schedulingContractors,
    });
    if (liveAnalysis.verdict === "blocked" && !canOverrideGating) {
      sonnerToast.error("Slot no longer available", {
        description: liveAnalysis.headline,
        duration: 6000,
      });
      return;
    }
    if (liveAnalysis.verdict === "blocked" && canOverrideGating) {
      sonnerToast.warning("Scheduling override applied", {
        description: liveAnalysis.headline,
        duration: 4500,
      });
    }

    const prior = {
      scheduledInstallDate: target.scheduledInstallDate,
      scheduledStartTime: target.scheduledStartTime,
      scheduledEndTime: target.scheduledEndTime,
      scheduledShifts: target.scheduledShifts,
      timezone: target.timezone,
      assignedCrew: target.assignedCrew,
      crewId: target.crewId,
      assignedSubcontractor: target.assignedSubcontractor,
      assignmentType: target.assignmentType,
    };
    const row = grid.rows.find(r => r.crewName === crewName);
    const isSub = row?.crewType === "Subcontractor";
    const rowCrewId =
      !isSub
        ? (crews.find((c) => c.name === crewName)?.id ?? target.crewId)
        : undefined;
    const scheduledShifts = clampInstallShifts(target.scheduledShifts ?? 1);
    const startDate = createInstallDateTime(dateIso, 9);
    const startIso = startDate.toISOString();
    const endIso = scheduledShifts > 1
      ? getInstallCalendarEndForShifts(startDate, scheduledShifts).toISOString()
      : createInstallDateTime(dateIso, 13).toISOString();
    const nextDispatchStatus =
      target.dispatchStatus === "Assigned" ||
      target.dispatchStatus === "Accepted" ||
      target.dispatchStatus === "Rejected" ||
      target.dispatchStatus === "Closed"
        ? target.dispatchStatus
        : "Assigned";

    if (
      dateIso !== (target.scheduledInstallDate || "") &&
      isPastInstallDate(dateIso)
    ) {
      const confirmed = window.confirm(
        `This will schedule ${getJobDisplayRef(target)} to a past install date (${dateIso}). Continue?`,
      );
      if (!confirmed) return;
    }

    // ── Execution Ready lock (checked FIRST) ──
    const bypassExecutionLock = isExternalManagedImport(target);
    if (!bypassExecutionLock && !isExecutionReady(target) && !canOverrideGating) {
      const readinessVerdict = canMarkJobReady({
        job: target,
        kit: getKit(materialKits, jobId),
        payProfiles,
        contractors: schedulingContractors,
        buildReviewEvents,
      });
      sonnerToast.warning(
        EXECUTION_LOCK_BLOCK_TITLE,
        getExecutionLockToastOptions(getExecutionLockBlockDescription(readinessVerdict)),
      );
    }
    if (!bypassExecutionLock && !isExecutionReady(target) && canOverrideGating) {
      sonnerToast.warning("Scheduling override applied", {
        description: EXECUTION_LOCK_BLOCK_DESCRIPTION,
        duration: 4500,
      });
    }

    // ── Material Alignment gate (spec === po === kit) ──
    const bypassMaterialGates = isExternalManagedImport(target);
    const alignment = assertMaterialAlignment(target, {
      kit: getKit(materialKits, jobId),
      orderDrafts,
      purchaseOrders,
    });
    if (!bypassMaterialGates && !alignment.ok && !canOverrideGating) {
      const copy = describeAlignmentBlock(alignment);
      sonnerToast.error(copy.title, { description: copy.description, duration: 6000 });
      return;
    }
    if (!bypassMaterialGates && !alignment.ok && canOverrideGating) {
      const copy = describeAlignmentBlock(alignment);
      sonnerToast.warning("Scheduling override applied", { description: copy.description, duration: 4500 });
    }

    // ── Pay Profile guard ── (only when binding a sub by name)
    if (isSub) {
      const guard = assertContractorAssignable({
        contractorName: crewName, contractors: schedulingContractors, projectType: target.projectType, payProfiles,
      });
      if (!guard.allowed && !canOverrideGating) {
        sonnerToast.error(guard.blockTitle, { description: guard.blockDescription, duration: 6000 });
        return;
      }
      if (!guard.allowed && canOverrideGating) {
        sonnerToast.warning("Scheduling override applied", { description: guard.blockDescription, duration: 4500 });
      }
    }

    // ── Material Kit dispatch guard ── (any commit-to-date counts)
    const kitGuard = assertJobKitReady({ kit: getKit(materialKits, jobId) });
    if (!bypassMaterialGates && !kitGuard.allowed && !canOverrideGating) {
      sonnerToast.warning("Scheduled with readiness warning", {
        description: kitGuard.blockDescription,
        duration: 6000,
      });
    }
    if (!bypassMaterialGates && !kitGuard.allowed && canOverrideGating) {
      sonnerToast.warning("Scheduling override applied", { description: kitGuard.blockDescription, duration: 4500 });
    }

    setJobs(prev => prev.map(j => j.id !== jobId ? j : ({
      ...j,
      scheduledInstallDate: dateIso,
      scheduledStartTime: startIso,
      scheduledEndTime: endIso,
      scheduledShifts,
      timezone: INSTALL_TIME_ZONE,
      assignedCrew: isSub ? "" : crewName,
      crewId: isSub ? undefined : rowCrewId,
      assignedSubcontractor: isSub ? crewName : "",
      assignmentType: isSub ? "Subcontractor" : "Internal Crew",
      dispatchStatus: nextDispatchStatus,
    })));

    JobService.update(jobId, {
      scheduledInstallDate: dateIso,
      scheduledStartTime: startIso,
      scheduledEndTime: endIso,
      scheduledShifts,
      timezone: INSTALL_TIME_ZONE,
      assignedCrew: isSub ? "" : crewName,
      crewId: isSub ? undefined : rowCrewId,
      assignedSubcontractor: isSub ? crewName : "",
      assignmentType: isSub ? "Subcontractor" : "Internal Crew",
      dispatchStatus: nextDispatchStatus,
      schedulingStatus: "Scheduled",
      currentStage: target.currentStage === ("Lead to Appointment" as Job["currentStage"]) ? ("Scheduled Tentative" as Job["currentStage"]) : target.currentStage,
    })
      .then(async ({ error }) => {
        if (error) throw error;
        await JobOperationsService.createSchedulingEvent({
          jobNumber: jobId,
          contractorId: isSub
            ? (schedulingContractors.find((c) => {
                if (c.companyName !== crewName) return false;
                const jobOrg = String((target as { organizationId?: string }).organizationId ?? "").trim();
                const contractorOrg = String((c as { organizationId?: string }).organizationId ?? "").trim();
                if (!jobOrg || !contractorOrg) return true;
                return jobOrg === contractorOrg;
              })?.id ?? null)
            : null,
          eventType: "schedule_set",
          startAt: startIso,
          endAt: endIso,
          status: "scheduled",
          notes: JSON.stringify({
            source: "plan_cockpit",
            crewName,
            assignmentType: isSub ? "Subcontractor" : "Internal Crew",
            previous: prior,
            next: {
              scheduledInstallDate: dateIso,
              scheduledStartTime: startIso,
              scheduledEndTime: endIso,
              scheduledShifts,
              timezone: INSTALL_TIME_ZONE,
              assignedCrew: isSub ? "" : crewName,
              assignedSubcontractor: isSub ? crewName : "",
              dispatchStatus: nextDispatchStatus,
            },
          }),
          briefSnapshot: getJobBriefing(target, getPortalSpec(target), await getJobPhotoUrls(target.id)) as unknown as Record<string, unknown>,
        });
      })
      .catch((error) => {
        console.error("[PlanCockpit] Failed to persist scheduling change:", error);
        setJobs(prev => prev.map(j => j.id !== jobId ? j : ({ ...j, ...prior })));
        sonnerToast.error("Schedule save failed", {
          description: "Crew/date was reverted. Please try again.",
        });
      });

    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);

    // If the live verdict downgraded to "warning", surface it accurately
    // and persist it on the cell so the justification doesn't disappear.
    if (liveAnalysis.verdict === "warning") {
      setWarningNotes(prev => {
        const next = new Map(prev);
        next.set(jobId, liveAnalysis.headline);
        return next;
      });
      sonnerToast.warning("Booked with warning", {
        description: liveAnalysis.headline,
      });
    } else {
      // Clean accepted-without-warning placement clears any stale note.
      setWarningNotes(prev => {
        if (!prev.has(jobId)) return prev;
        const next = new Map(prev);
        next.delete(jobId);
        return next;
      });
    }

    const undoToast = toast({
      title: "Booked",
      description: `${target.customerName} → ${crewName} on ${format(parseISO(dateIso), "EEE MMM d")} · press Z to undo`,
    });
    const onUndo = (e: KeyboardEvent) => {
      if (e.key !== "z" && e.key !== "Z") return;
      const tgt = e.target as HTMLElement | null;
      if (tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable)) return;
      setJobs(prev => prev.map(j => j.id !== jobId ? j : ({ ...j, ...prior })));
      toast({ title: "Reverted", description: `${target.customerName} returned to Ready to book.` });
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener("keydown", onUndo);
      try { undoToast.dismiss?.(); } catch { /* noop */ }
    };
    window.addEventListener("keydown", onUndo);
    undoTimerRef.current = setTimeout(cleanup, 8000);

    // ── Open Confirmation Inspector — meaningful actions only ──────
    // Rail→cell drops, conflict-resolution apply, and major reschedules
    // (cell→cell where date or crew changed) all qualify. The no-op guard
    // above already returns before this point, so any drop reaching here
    // is, by definition, a state change.
    const isMajorChange =
      source === "rail" ||
      source === "conflict" ||
      prior.scheduledInstallDate !== dateIso ||
      (prior.assignedCrew || prior.assignedSubcontractor) !== crewName;
    if (isMajorChange) {
      const trigger =
        source === "rail" ? "drop_from_rail" :
        source === "conflict" ? "conflict_resolved" :
        "reschedule";
      setInspectorCtx({
        jobId,
        trigger,
        analysis: liveAnalysis,
        warningNote: liveAnalysis.verdict === "warning" ? liveAnalysis.headline : null,
      });
    }
  }, [
    jobs,
    setJobs,
    grid,
    canOverrideGating,
    schedulingContractors,
    payProfiles,
    materialKits,
    orderDrafts,
    purchaseOrders,
  ]);

  // ── Cell-peek handlers ─────────────────────────────────────────
  const previewSpan = useMemo(() => {
    if (!extendTarget || previewShifts == null) return null;
    const start = extendTarget.job.scheduledInstallDate;
    if (!start) return null;
    const dates: string[] = [];
    let cursor = parseISO(start);
    for (let i = 0; i < previewShifts; i++) {
      dates.push(format(cursor, "yyyy-MM-dd"));
      cursor = addDays(cursor, 1);
      while (cursor.getDay() === 0 || cursor.getDay() === 6) cursor = addDays(cursor, 1);
    }
    return { jobId: extendTarget.job.id, crewName: extendTarget.crewRow?.crewName ?? null, dates };
  }, [extendTarget, previewShifts]);

  const handleExtendCell = useCallback((cell: CapacityCell) => {
    if (!cell.job) return;
    const crewRow = grid.rows.find((r) => r.crewName === cell.crewName) ?? null;
    setExtendTarget({ job: cell.job, crewRow });
  }, [grid.rows]);

  const handleRescheduleCell = useCallback((cell: CapacityCell) => {
    if (cell.job) setRescheduleJob(cell.job);
  }, []);

  const handleConfirmCell = useCallback((cell: CapacityCell) => {
    const job = cell.job;
    if (!job || job.currentStage === "Completed") return;
    const prior = {
      currentStage: job.currentStage,
      schedulingStatus: job.schedulingStatus,
      dispatchStatus: job.dispatchStatus,
    };
    setJobs((prev) =>
      prev.map((j) =>
        j.id !== job.id
          ? j
          : {
              ...j,
              currentStage: "Scheduled Confirmed" as Job["currentStage"],
              schedulingStatus: "Confirmed",
              dispatchStatus: "Assigned",
            },
      ),
    );
    void (async () => {
      const { error } = await JobService.update(job.id, {
        currentStage: "Scheduled Confirmed" as Job["currentStage"],
        schedulingStatus: "Confirmed",
        dispatchStatus: "Assigned",
      });
      if (!error) {
        sonnerToast.success("Schedule confirmed", { description: `${job.customerName} · dispatch confirmed` });
        return;
      }

      // Conflict case: backend may still have accepted via concurrent writer.
      // Reconcile from source-of-truth before showing failure.
      const { data: latest, error: readError } = await JobService.getById(job.id);
      if (!readError && latest) {
        setJobs((prev) => prev.map((j) => (j.id !== job.id ? j : { ...j, ...latest })));
        if (latest.currentStage === "Scheduled Confirmed" && latest.schedulingStatus === "Confirmed") return;
      } else {
        setJobs((prev) => prev.map((j) => (j.id !== job.id ? j : { ...j, ...prior })));
      }

      console.error("[PlanCockpit] Failed to persist confirm:", error);
      sonnerToast.error("Confirm failed", {
        description: getStageGuardMessage(error, "Could not persist confirmation."),
      });
    })();
  }, [setJobs]);

  const handleStartInstallCell = useCallback((cell: CapacityCell) => {
    const job = cell.job;
    if (!job || job.currentStage === "Completed") return;
    const prior = {
      currentStage: job.currentStage,
      installStatus: job.installStatus,
      schedulingStatus: job.schedulingStatus,
      dispatchStatus: job.dispatchStatus,
    };
    setJobs((prev) =>
      prev.map((j) =>
        j.id !== job.id
          ? j
          : {
              ...j,
              currentStage: "In Progress" as Job["currentStage"],
              installStatus: "In Progress",
              schedulingStatus: "Confirmed",
              dispatchStatus: "Assigned",
            },
      ),
    );
    void (async () => {
      const { error } = await JobService.update(job.id, {
        currentStage: "In Progress" as Job["currentStage"],
        installStatus: "In Progress",
        schedulingStatus: "Confirmed",
        dispatchStatus: "Assigned",
      });
      if (!error) {
        sonnerToast.success("Install started", { description: `${job.customerName} · in progress` });
        return;
      }

      const { data: latest, error: readError } = await JobService.getById(job.id);
      if (!readError && latest) {
        setJobs((prev) => prev.map((j) => (j.id !== job.id ? j : { ...j, ...latest })));
        if (latest.currentStage === "In Progress") return;
      } else {
        setJobs((prev) => prev.map((j) => (j.id !== job.id ? j : { ...j, ...prior })));
      }

      console.error("[PlanCockpit] Failed to persist start install:", error);
      sonnerToast.error("Start failed", {
        description: getStageGuardMessage(error, "Could not persist install start."),
      });
    })();
  }, [setJobs]);

  const handleMarkCompleteCell = useCallback((cell: CapacityCell) => {
    const job = cell.job;
    if (!job || job.currentStage === "Completed") return;

    // ── Lifecycle gate enforcement ───────────────────────────────────
    // Plan cockpit is NOT allowed to bypass the installation lifecycle.
    // Completion requires a wrap record with outcome "completed_today" AND
    // a settlement status that doesn't hold the job (no open issue/finance
    // hold). Without these, finance, commissions, and CX see an inconsistent
    // state for a job the grid claims is done.
    const wraps = installerWrapRecords.filter(w => w.jobId === job.id);
    const latestWrap = wraps.length > 0
      ? [...wraps].sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0]
      : null;

    if (!latestWrap) {
      sonnerToast.error("Cannot mark complete from Plan", {
        description: "Crew must submit an end-of-day wrap before this job can be completed.",
        duration: 6000,
      });
      return;
    }
    if (latestWrap.outcome !== "completed_today") {
      sonnerToast.error("Wrap outcome blocks completion", {
        description: `Latest wrap is "${latestWrap.outcome.replace(/_/g, " ")}". Only a completed_today wrap can finalize.`,
        duration: 6000,
      });
      return;
    }
    if (latestWrap.settlementStatus === "hold_issue") {
      sonnerToast.error("Settlement hold — cannot complete", {
        description: "An open issue or finance hold is preventing completion. Resolve from the job detail.",
        duration: 6000,
      });
      return;
    }
    if (!latestWrap.serviceChecklist || (
      !latestWrap.serviceChecklist.overridden && (
        !latestWrap.serviceChecklist.visualInspection ||
        !latestWrap.serviceChecklist.functionalityConfirmed ||
        !latestWrap.serviceChecklist.materialsInstalledCorrectly ||
        !latestWrap.serviceChecklist.customerWalkthrough
      )
    )) {
      sonnerToast.error("Service prevention checklist incomplete", {
        description: "All 4 checklist items (or override) are required before completion.",
        duration: 6000,
      });
      return;
    }

    const previousStage = job.currentStage;
    setJobs((prev) =>
      prev.map((j) =>
        j.id !== job.id
          ? j
          : {
              ...j,
              currentStage: "Completed" as Job["currentStage"],
              installStatus: "Install Complete" as Job["installStatus"],
              activityHistory: [
                ...j.activityHistory,
                {
                  id: `cmpl-${Date.now()}`,
                  date: new Date().toISOString(),
                  action: "Install marked complete from Plan grid",
                  user: "Dispatch",
                  noteType: "ops" as const,
                },
              ],
            },
      ),
    );
    sonnerToast.success(`${job.customerName} · install complete`, {
      duration: 8000,
      action: {
        label: "Undo",
        onClick: () => {
          setJobs((prev) =>
            prev.map((j) => (j.id !== job.id ? j : { ...j, currentStage: previousStage })),
          );
        },
      },
    });
  }, [setJobs, installerWrapRecords]);

  /**
   * Return-to-queue: clears scheduling fields so the job pops back into
   * the Ready rail. Optimistic + 8s sonner Undo (consistent with bookJobToCell).
   * Refuses jobs already in-progress / completed (guarded in popover too).
   */
  const returnJobToQueue = useCallback((jobId: string) => {
    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;
    if (job.currentStage === "Completed") return;
    const prior = {
      scheduledInstallDate: job.scheduledInstallDate,
      assignedCrew: job.assignedCrew,
      crewId: job.crewId,
      assignedSubcontractor: job.assignedSubcontractor,
      assignmentType: job.assignmentType,
    };
    setJobs((prev) =>
      prev.map((j) =>
        j.id !== jobId
          ? j
          : {
              ...j,
              scheduledInstallDate: "",
              assignedCrew: "",
              crewId: undefined,
              assignedSubcontractor: "",
              activityHistory: [
                ...j.activityHistory,
                {
                  id: `rtq-${Date.now()}`,
                  date: new Date().toISOString(),
                  action: "Returned to Ready queue from Plan grid",
                  user: "Dispatch",
                  noteType: "ops" as const,
                },
              ],
            },
      ),
    );
    JobService.update(jobId, {
      scheduledInstallDate: "",
      scheduledStartTime: undefined,
      scheduledEndTime: undefined,
      assignedCrew: "",
      crewId: undefined,
      assignedSubcontractor: "",
      schedulingStatus: "Unscheduled",
    })
      .then(async ({ error }) => {
        if (error) throw error;
        await JobOperationsService.createSchedulingEvent({
          jobNumber: jobId,
          contractorId: null,
          eventType: "schedule_removed",
          startAt: new Date().toISOString(),
          endAt: null,
          status: "cancelled",
          notes: JSON.stringify({
            source: "plan_cockpit",
            reason: "returned_to_queue",
            previous: prior,
            next: {
              scheduledInstallDate: "",
              assignedCrew: "",
              assignedSubcontractor: "",
              assignmentType: prior.assignmentType,
            },
          }),
        });
      })
      .catch((error) => {
        console.error("[PlanCockpit] Failed to persist return-to-queue:", error);
        setJobs((prev) => prev.map((j) => (j.id !== jobId ? j : { ...j, ...prior })));
        sonnerToast.error("Queue return save failed", {
          description: "The job assignment was restored.",
        });
      });
    // Clear any stale warning justification — job is no longer placed.
    setWarningNotes(prev => {
      if (!prev.has(jobId)) return prev;
      const next = new Map(prev);
      next.delete(jobId);
      return next;
    });
    sonnerToast.success(`${job.customerName} · returned to queue`, {
      duration: 8000,
      action: {
        label: "Undo",
        onClick: () => {
          setJobs((prev) => prev.map((j) => (j.id !== jobId ? j : { ...j, ...prior })));
        },
      },
    });
  }, [jobs, setJobs]);

  const handleReturnToQueue = useCallback((cell: CapacityCell) => {
    if (!cell.job) return;
    if (cell.state === "in_progress") return;
    returnJobToQueue(cell.job.id);
  }, [returnJobToQueue]);


  const goPrevWeek = () => {
    setReferenceDate((d) => addDays(d, -7));
    setRangeStart((d) => format(addDays(parseISO(d), -7), "yyyy-MM-dd"));
    setRangeEnd((d) => format(addDays(parseISO(d), -7), "yyyy-MM-dd"));
  };
  const goNextWeek = () => {
    setReferenceDate((d) => addDays(d, 7));
    setRangeStart((d) => format(addDays(parseISO(d), 7), "yyyy-MM-dd"));
    setRangeEnd((d) => format(addDays(parseISO(d), 7), "yyyy-MM-dd"));
  };
  const goToday = () => {
    const now = new Date();
    setReferenceDate(now);
    setRangeStart(format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"));
    setRangeEnd(format(endOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"));
  };
  const applyDateRange = useCallback(() => {
    const start = parseISO(rangeStart);
    if (!isValid(start)) return;
    setReferenceDate(start);
  }, [rangeStart]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape" && mapFullscreen) { setMapFullscreen(false); return; }
      if (e.key === "ArrowLeft") goPrevWeek();
      else if (e.key === "ArrowRight") goNextWeek();
      else if (e.key === "t" || e.key === "T") goToday();
      else if (e.key === "m" || e.key === "M") setShowMap(v => !v);
      else if (e.key === "f" || e.key === "F") setMapFullscreen(v => !v);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mapFullscreen]);

  useEffect(() => { setMapDayFilter(null); setFocusJobId(null); }, [referenceDate]);

  // Persist referenceDate to URL (?week=YYYY-MM-DD) so lens-switching survives.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const iso = format(referenceDate, "yyyy-MM-dd");
    if (sp.get("week") === iso) return;
    sp.set("week", iso);
    const next = `${window.location.pathname}?${sp.toString()}${window.location.hash}`;
    window.history.replaceState(null, "", next);
  }, [referenceDate]);

  // Deep-link helper: /scheduling-dispatch?lens=plan&job=<jobId>
  // Opens booking dialog preselected to that job for faster assignment flow.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const jobId = sp.get("job");
    if (!jobId) return;
    const exists = jobs.some((j) => j.id === jobId);
    if (!exists) return;
    const nextIso = formatInstallDateIso(addDays(referenceDate, 1));
    const d = createInstallDateTime(nextIso, 9);
    setPreselectJobId(jobId);
    setBookingSlot(d);
    setBookingOpen(true);
    sp.delete("job");
    const next = `${window.location.pathname}?${sp.toString()}${window.location.hash}`;
    window.history.replaceState(null, "", next);
  }, [jobs, referenceDate]);

  // ── KPI Drilldown → grid focus bridge ──────────────────────────
  // The KpiDrilldownDrawer dispatches `koredor:focus-grid` with either a
  // cell key (`crewName|dateIso`) or a crew row name. We scroll-into-view
  // and apply a transient highlight ring (~2.5s). No state mutation —
  // purely visual feedback via inline style on the matched DOM node.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as
        | { kind: "cell"; crewName: string; dateIso: string }
        | { kind: "row"; crewName: string }
        | undefined;
      if (!detail) return;
      requestAnimationFrame(() => {
        const selector = detail.kind === "cell"
          ? `[data-cell-key="${CSS.escape(`${detail.crewName}|${detail.dateIso}`)}"]`
          : `[data-crew-row="${CSS.escape(detail.crewName)}"]`;
        const el = document.querySelector<HTMLElement>(selector);
        if (!el) {
          sonnerToast("Not on this week", {
            description: "The job's current schedule is outside the visible week.",
            duration: 3500,
          });
          return;
        }
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        const prevShadow = el.style.boxShadow;
        const prevTransition = el.style.transition;
        el.style.transition = "box-shadow 200ms ease";
        el.style.boxShadow = "inset 0 0 0 2px hsl(var(--primary) / 0.55), 0 0 0 4px hsl(var(--primary) / 0.18)";
        window.setTimeout(() => {
          el.style.boxShadow = prevShadow;
          el.style.transition = prevTransition;
        }, 2500);
      });
    };
    window.addEventListener("koredor:focus-grid", handler as EventListener);
    return () => window.removeEventListener("koredor:focus-grid", handler as EventListener);
  }, []);

  const weekLabel = `${format(weekStart, "MMM d")} – ${format(weekEnd, "MMM d, yyyy")}`;


  return (
    <TooltipProvider delayDuration={150}>
      {/* ── Toolbar: hairline strip, calm rhythm ─────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap py-3 border-b border-border/60">
        <div className="flex items-center gap-3">
          <div className="flex items-center">
            <button
              type="button"
              onClick={goPrevWeek}
              className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-[12px] font-medium text-foreground px-2 tabular-nums min-w-[170px] text-center">
              {weekLabel}
            </span>
            <button
              type="button"
              onClick={goNextWeek}
              className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded"
              aria-label="Next week"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {!isCurrentWeek && (
            <button
              type="button"
              onClick={goToday}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronsRight className="h-3 w-3" />
              Today
            </button>
          )}
          <span className="hidden md:inline-flex items-center gap-3 pl-3 border-l border-border/60 text-[11px] text-muted-foreground">
            <span><span className="text-foreground font-medium tabular-nums">{readyJobs.length}</span> ready</span>
            <span><span className="text-foreground font-medium tabular-nums">{utilization}%</span> utilization</span>
            <span className="tabular-nums"><span className="text-foreground font-medium">{bookedDays}</span>/{capacityDays} install-days</span>
          </span>
          <div className="flex items-center gap-1.5 pl-3 border-l border-border/60">
            <input
              type="date"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              className="h-7 w-[130px] rounded border border-border bg-background px-2 text-[11px] text-foreground"
              aria-label="Range start"
            />
            <ArrowRight className="h-3 w-3 text-muted-foreground" />
            <input
              type="date"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              className="h-7 w-[130px] rounded border border-border bg-background px-2 text-[11px] text-foreground"
              aria-label="Range end"
            />
            <button
              type="button"
              onClick={applyDateRange}
              className="h-7 rounded border border-border px-2 text-[11px] text-foreground hover:bg-muted transition-colors"
            >
              Apply
            </button>
          </div>
          <input
            value={planSearch}
            onChange={(e) => setPlanSearch(e.target.value)}
            placeholder="Search jobs, customer, crew..."
            className="h-7 w-[240px] rounded border border-border bg-background px-2 text-[11px] text-foreground placeholder:text-muted-foreground"
            aria-label="Search plan jobs"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowMap(v => !v)}
          className={cn(
            "inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 transition-colors",
            showMap ? "text-primary" : "text-muted-foreground hover:text-foreground",
          )}
        >
          <MapIcon className="h-3.5 w-3.5" />
          {showMap ? "Hide map" : "Show map"}
          <span className="ml-1 text-[9.5px] opacity-60 hidden md:inline">M</span>
        </button>
      </div>

      {/* ── Unscheduled-issue alert strip ──────────────────────────
           Surfaces conflicts that have no grid cell (no_crew / no_schedule).
           Operator sees these BEFORE the grid; clicking opens the
           conflict-resolution drawer scoped to that job. */}
      {unscheduledIssueJobs.length > 0 && (
        <div className="flex items-start gap-3 px-4 py-2.5 border-b border-warning/40 bg-warning/[0.06]">
          <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-semibold text-foreground">
              {unscheduledIssueJobs.length} job{unscheduledIssueJobs.length === 1 ? "" : "s"} need{unscheduledIssueJobs.length === 1 ? "s" : ""} attention — invisible on the grid
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Missing crew assignment or install date. Won't appear in any cell until resolved.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {unscheduledIssueJobs.slice(0, 8).map(j => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => setConflictJobId(j.id)}
                  className="text-[10.5px] px-2 py-0.5 rounded border border-warning/40 bg-background hover:bg-warning/10 text-foreground transition-colors"
                >
                  {getJobDisplayRef(j)} · {j.customerName}
                </button>
              ))}
              {unscheduledIssueJobs.length > 8 && (
                <span className="text-[10.5px] px-2 py-0.5 text-muted-foreground">
                  +{unscheduledIssueJobs.length - 8} more
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Top row: rail + grid (seamless, hairline-divided) ─────── */}
      <div className="grid grid-cols-[260px_1fr] border-b border-border/60">
        {/* Ready rail — also a drop target so cell-origin drags can return a job to queue. */}
        <aside
          className={cn(
            "border-r border-border/60 flex flex-col min-h-0 transition-colors",
            // Always-on dashed inner border on the rail surface signals it's a
            // valid drop target for booked jobs (return-to-queue), even when
            // the operator isn't actively dragging.
            "ring-1 ring-inset ring-transparent",
            draggingJobId && dragOrigin === "cell" && "bg-warning/10 ring-warning/60",
          )}
          onDragOver={(e) => {
            // Only accept drags that originated from a booked cell — rail-origin
            // drags shouldn't loop back to the rail.
            if (!draggingJobId || dragOrigin !== "cell") return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
          }}
          onDrop={(e) => {
            if (dragOrigin !== "cell") return;
            const id = e.dataTransfer.getData(DRAG_MIME);
            if (!id) return;
            e.preventDefault();
            returnJobToQueue(id);
            endDrag();
          }}
        >
          <header className="px-4 py-3 border-b border-border/60">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
                Ready to book
              </p>
              <span
                className="inline-flex items-center gap-1 text-[9.5px] text-muted-foreground/80"
                title="Drag any booked job here from the grid to return it to the queue."
              >
                <Undo2 className="h-2.5 w-2.5" />
                drop here to unbook
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground/80 mt-1">
              {draggingJobId && dragOrigin === "cell"
                ? "Release to return this job to the queue"
                : filteredReadyJobs.length === 0 && filteredAlmostReadyJobs.length === 0
                  ? "No unscheduled jobs"
                  : `${filteredReadyJobs.length} ready · ${filteredAlmostReadyJobs.length} almost ready`}
            </p>
          </header>
          <div className="overflow-y-auto flex-1 max-h-[520px]">
            {filteredReadyJobs.length === 0 && filteredAlmostReadyJobs.length === 0 && (
              <div className="px-4 py-10 text-center text-[11px] text-muted-foreground">
                {normalizedPlanSearch ? "No jobs match your search." : "Nothing waiting. Inbox zero."}
              </div>
            )}
            {/* ── Section A: truly ready ─────────────────────────────
                 Stronger affordances — left accent, full opacity,
                 draggable. Operator can scan these as "safe to place". */}
            {filteredReadyJobs.length > 0 && (
              <div className="px-3 pt-2 pb-1 text-[9.5px] uppercase tracking-[0.14em] text-success/80 font-semibold flex items-center gap-1.5">
                <span className="h-1 w-1 rounded-full bg-success" />
                Ready · safe to place
              </div>
            )}
            {filteredReadyJobs.map(({ job, daysSinceSold, revenue }) => (
              (() => {
                const hasAssignee = Boolean(
                  String(job.assignedCrew ?? "").trim() ||
                  String(job.assignedSubcontractor ?? "").trim() ||
                  String(job.crewId ?? "").trim(),
                );
                const isAssignedUnscheduled = !job.scheduledInstallDate && hasAssignee;
                return (
              <div
                key={job.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_MIME, job.id);
                  e.dataTransfer.effectAllowed = "move";
                  beginDrag(job.id, "rail");
                }}
                onDragEnd={endDrag}
                className={cn(
                  "border-b border-border/40 border-l-2 border-l-success/70 hover:bg-success/[0.04] transition-colors group cursor-grab active:cursor-grabbing",
                  draggingJobId === job.id && "opacity-40",
                )}
              >
                <button
                  type="button"
                  onClick={() => openJobBooking(job.id)}
                  className="w-full text-left px-4 py-3"
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="h-3 w-3 text-muted-foreground/60 mt-1 shrink-0 group-hover:text-foreground transition-colors" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-medium text-foreground truncate">
                        {getJobDisplayRef(job)} · {job.customerName}
                      </p>
                      <p className="text-[10.5px] text-muted-foreground truncate mt-0.5">
                        {job.projectType} · {job.address.city}
                      </p>
                      {isAssignedUnscheduled && (
                        <div className="mt-1">
                          <span className="inline-flex items-center rounded border border-info/30 bg-info/10 px-1.5 py-0.5 text-[9px] font-medium text-info">
                            Assigned · Unscheduled
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 mt-1.5">
                        <span className="text-[10px] text-muted-foreground tabular-nums">
                          {daysSinceSold}d old
                        </span>
                        <span className="text-[10px] font-medium text-foreground tabular-nums">
                          {fmtMoney(revenue)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-foreground transition-colors mt-1 shrink-0" />
                  </div>
                </button>
              </div>
                );
              })()
            ))}

            {/* ── Section B: almost ready ────────────────────────────
                 Dimmed, non-draggable, with the top blocker visible so the
                 operator can scan WHY it isn't placeable yet. Distinct
                 from ready group at a glance. */}
            {filteredAlmostReadyJobs.length > 0 && (
              <div className="px-3 pt-3 pb-1 mt-1 border-t border-border/40 text-[9.5px] uppercase tracking-[0.14em] text-warning/80 font-semibold flex items-center gap-1.5">
                <span className="h-1 w-1 rounded-full bg-warning" />
                Almost ready · clear blocker first
              </div>
            )}
            {filteredAlmostReadyJobs.map(({ job, daysSinceSold, revenue, topBlocker }) => (
              (() => {
                const hasAssignee = Boolean(
                  String(job.assignedCrew ?? "").trim() ||
                  String(job.assignedSubcontractor ?? "").trim() ||
                  String(job.crewId ?? "").trim(),
                );
                const isAssignedUnscheduled = !job.scheduledInstallDate && hasAssignee;
                const kitMissing = !getKit(materialKits, job.id);
                const shouldShowGenerateKit =
                  !!job.boundJobSpec &&
                  kitMissing &&
                  String(topBlocker ?? "").toLowerCase().includes("kit");
                return (
              <div
                key={job.id}
                className="border-b border-border/40 border-l-2 border-l-warning/40 bg-muted/[0.15] opacity-75 hover:opacity-100 transition-opacity"
                title="Clear the blocker before this job can be dragged onto the grid."
              >
                <div className="px-4 py-2.5">
                  <button
                    type="button"
                    onClick={() => openJobBooking(job.id)}
                    className="w-full text-left cursor-default"
                  >
                  <div className="flex items-start gap-2">
                    <span className="h-3 w-3 mt-1 shrink-0 flex items-center justify-center">
                      <AlertTriangle className="h-2.5 w-2.5 text-warning/70" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11.5px] font-medium text-foreground/85 truncate">
                        {getJobDisplayRef(job)} · {job.customerName}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                        {job.projectType} · {job.address.city}
                      </p>
                      {topBlocker && (
                        <p className="text-[10px] text-warning/90 truncate mt-0.5">
                          ⚠ {topBlocker}
                        </p>
                      )}
                      {isAssignedUnscheduled && (
                        <div className="mt-1">
                          <span className="inline-flex items-center rounded border border-info/25 bg-info/10 px-1.5 py-0.5 text-[9px] font-medium text-info/90">
                            Assigned · Unscheduled
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="text-[9.5px] text-muted-foreground tabular-nums">
                          {daysSinceSold}d old
                        </span>
                        <span className="text-[9.5px] text-muted-foreground tabular-nums">
                          {fmtMoney(revenue)}
                        </span>
                      </div>
                    </div>
                  </div>
                  </button>
                  {shouldShowGenerateKit && (
                    <div className="mt-2 pl-5">
                      <Link
                        to={`/jobs/${job.id}`}
                        className="inline-flex h-6 items-center rounded border border-primary/40 px-2 text-[10px] text-primary hover:bg-primary/10 transition-colors"
                      >
                        Resolve In Job
                      </Link>
                    </div>
                  )}
                </div>
              </div>
                );
              })()
            ))}
          </div>
        </aside>

        {/* Week capacity grid — table-fixed for equal columns */}
        <section className="flex flex-col min-h-0">
          <header className="px-4 py-3 border-b border-border/60 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
                This week
              </p>
              <p className="text-[11px] text-muted-foreground/80 mt-1">
                Click a job to inspect · drag the grip ⋮⋮ to move · drop on the rail to return to queue.
              </p>
              <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                Active range: {format(rangeStartDate, "MMM d, yyyy")} - {format(rangeEndDate, "MMM d, yyyy")}
              </p>
            </div>
            <div className="flex items-center gap-3 text-[10.5px] text-muted-foreground">
              <Legend dot="bg-success/70" label="Ready" />
              <Legend dot="bg-warning/70" label="Tentative" />
              <Legend dot="bg-info/70" label="In progress" />
              <Legend dot="bg-destructive border border-destructive" label="Conflict" strong />
            </div>
          </header>

          <div className="overflow-x-auto">
            <table className="w-full table-fixed border-collapse">
              <colgroup>
                <col style={{ width: "160px" }} />
                {grid.days.map(d => (
                  <col key={d.iso} style={{ width: `calc((100% - 160px) / ${grid.days.length})` }} />
                ))}
              </colgroup>
              <thead>
                <tr className="border-b border-border/60">
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground sticky left-0 bg-background z-10">
                    Crew
                  </th>
                  {grid.days.map(d => {
                    // Aggregate availability signals for this date across all contractors
                    let hasUnavailable = false;
                    let hasAvailable = false;
                    for (const byDate of availabilityByContractor.values()) {
                      const a = byDate.get(d.iso);
                      if (!a) continue;
                      if (a.is_available) hasAvailable = true;
                      else hasUnavailable = true;
                    }
                    return (
                      <th
                        key={d.iso}
                        className={cn(
                          "px-2 py-2.5 text-[10px] uppercase tracking-[0.16em] font-medium text-center",
                          d.isToday ? "text-primary bg-primary/[0.04]" : "text-muted-foreground",
                        )}
                      >
                        {d.label}
                        {(hasUnavailable || hasAvailable) && (
                          <div className="flex justify-center gap-0.5 mt-1">
                            {hasUnavailable && <span className="w-1.5 h-1.5 rounded-full bg-destructive inline-block" title="Contractor unavailable this day" />}
                            {hasAvailable  && <span className="w-1.5 h-1.5 rounded-full bg-success inline-block" title="Contractor available this day" />}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grid.rows.length === 0 && (
                  <tr>
                    <td colSpan={1 + grid.days.length} className="px-4 py-10 text-center text-[11px] text-muted-foreground">
                      No active crews. Approve contractors or add internal crews to start planning.
                    </td>
                  </tr>
                )}
                {filteredDisplayRows.map(row => (
                  <tr
                    key={row.crewName}
                    data-crew-row={row.crewName}
                    className="border-b border-border/40 last:border-0 transition-shadow"
                  >
                    <td className="px-4 py-2 sticky left-0 bg-background z-10">
                      {row.crewName === UNASSIGNED_ROW_KEY ? (
                        <div className="flex items-center gap-1.5">
                          <UserX className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                          <p className="text-[12px] font-medium text-muted-foreground">Unassigned</p>
                        </div>
                      ) : (
                        <>
                          <p className="text-[12px] font-medium text-foreground truncate">
                            {row.crewName}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                            <span>{row.crewType === "Subcontractor" ? "Sub" : "Crew"}</span>
                            <span className="text-border">·</span>
                            <span className={cn(
                              row.utilizationPct > 100 ? "text-destructive" :
                              row.utilizationPct >= 80 ? "text-warning" :
                              row.utilizationPct >= 40 ? "text-success" :
                              "text-muted-foreground",
                            )}>
                              {row.utilizationPct}%
                            </span>
                          </p>
                        </>
                      )}
                    </td>
                    {row.cells.map(cell => {
                      const day = grid.days.find(d => d.iso === cell.dateIso);
                      const isDropping = !!draggingJobId;
                      const isHovered =
                        hoverCell?.crewName === row.crewName &&
                        hoverCell?.dateIso === cell.dateIso;
                      // Precomputed verdict drives the always-on highlight when dragging.
                      const precomputedVerdict = isDropping
                        ? dropVerdictMap.get(`${row.crewName}|${cell.dateIso}`)
                        : undefined;
                      const verdict = isHovered ? dropAnalysis?.verdict : precomputedVerdict;
                      const availSignal = (() => {
                        const cid = contractorIdByName.get(row.crewName);
                        if (!cid) return null;
                        return availabilityByContractor.get(cid)?.get(cell.dateIso) ?? null;
                      })();
                      return (
                        <td
                          key={cell.dateIso}
                          data-cell-key={`${row.crewName}|${cell.dateIso}`}
                          className={cn(
                            "p-1 align-top transition-shadow relative",
                            day?.isToday && "bg-primary/[0.025]",
                            row.crewName === UNASSIGNED_ROW_KEY && cell.state === "open" && "border border-dashed border-muted-foreground/20 rounded",
                            availSignal?.is_available === true  && "bg-success/20 border-l-2 border-l-success",
                            availSignal?.is_available === false && "bg-destructive/20 border-l-2 border-l-destructive",
                          )}
                          onDragOver={(e) => {
                            if (!isDropping) return;
                            if (!isHovered) {
                              setHoverCell({ crewName: row.crewName, dateIso: cell.dateIso });
                            }
                            // Use precomputed verdict to allow/deny — no need to re-run analyze every frame.
                            if (precomputedVerdict === "blocked") {
                              e.dataTransfer.dropEffect = "none";
                              return;
                            }
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                          }}
                          onDragLeave={(e) => {
                            // Containment check: ignore leave events that fire when
                            // the cursor crosses internal padding/child element
                            // boundaries within the same cell. Only clear hover
                            // when truly leaving the cell. Without this, the drop
                            // hint flickers visibly during slow drags.
                            const next = e.relatedTarget as Node | null;
                            const cellEl = e.currentTarget;
                            if (next && cellEl.contains(next)) return;
                            if (isHovered) setHoverCell(null);
                          }}
                          onDrop={(e) => {
                            const id = e.dataTransfer.getData(DRAG_MIME);
                            if (!id) return;
                            // Hard-stop only the cached "blocked" path; the
                            // commit-time re-check inside bookJobToCell is the
                            // single source of truth for warning/blocked outcomes
                            // (handles stale-verdict races).
                            const cachedVerdict = dropVerdictMap.get(`${row.crewName}|${cell.dateIso}`);
                            if (cachedVerdict === "blocked") {
                              endDrag();
                              return;
                            }
                            e.preventDefault();
                            // Capture origin BEFORE endDrag clears it — the
                            // inspector trigger depends on whether this was a
                            // rail-origin placement or a cell-to-cell move.
                            const origin = dragOrigin;
                            endDrag();
                            bookJobToCell(id, row.crewName, cell.dateIso, origin === "rail" ? "rail" : "cell");
                          }}
                        >
                          {availSignal && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className={cn(
                                  "absolute top-1 right-1 z-10 px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider cursor-default select-none",
                                  availSignal.is_available
                                    ? "bg-success text-white"
                                    : "bg-destructive text-white",
                                )}>
                                  {availSignal.is_available ? "✓" : "✕"}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs max-w-[200px]">
                                <p className="font-semibold">{availSignal.is_available ? "Available" : "Unavailable"}</p>
                                {availSignal.note && <p className="text-muted-foreground mt-0.5">{availSignal.note}</p>}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {cell.job ? (
                            <CellPeekPopover
                              cell={cell}
                              onExtend={() => handleExtendCell(cell)}
                              onReschedule={() => handleRescheduleCell(cell)}
                              onConfirm={() => handleConfirmCell(cell)}
                              onStartInstall={() => handleStartInstallCell(cell)}
                              onComplete={() => handleMarkCompleteCell(cell)}
                              onResolveConflict={() => setConflictJobId(cell.job!.id)}
                              onReturnToQueue={() => handleReturnToQueue(cell)}
                              warningNote={warningNotes.get(cell.job.id) ?? null}
                              suggestion={
                                // Lightweight Recommendation Layer — popover
                                // surfaces at most 1 suggestion (no crew load).
                                // Built from already-computed grid + risk data.
                                buildSchedulingSuggestions({
                                  job: cell.job,
                                  currentCrew: row.crewName,
                                  dateIso: cell.dateIso,
                                  grid,
                                  jobs,
                                  eventLog,
                                  auditTrail,
                                  max: 1,
                                  includeCrewLoad: false,
                                })[0] ?? null
                              }
                              onAcceptSuggestion={(s: SchedulingSuggestion) => {
                                if (s.kind !== "better_crew" || !s.proposedCrew || !cell.job) return;
                                // Open Inspector pre-filled with the proposed crew.
                                // Operator confirms via the in-Inspector "Switch" CTA.
                                setInspectorCtx({
                                  jobId: cell.job.id,
                                  trigger: "reschedule",
                                  analysis: null,
                                  warningNote: null,
                                  proposedCrew: s.proposedCrew,
                                });
                              }}
                            >
                              <Cell
                                cell={cell}
                                onOpenSlot={() => openCellBooking(cell.dateIso)}
                                isDropTarget={isDropping && verdict === "valid"}
                                isDropWarning={isDropping && verdict === "warning"}
                                isDropBlocked={isDropping && verdict === "blocked"}
                                hoverHint={isHovered ? dropAnalysis?.headline ?? null : null}
                                draggingJobId={draggingJobId}
                                dragOrigin={dragOrigin}
                                isPreviewExtension={
                                  !!previewSpan
                                  && previewSpan.jobId === cell.job.id
                                  && previewSpan.crewName === row.crewName
                                  && previewSpan.dates.includes(cell.dateIso)
                                }
                                warningNote={warningNotes.get(cell.job.id) ?? null}
                                isClean={
                                  (cell.state === "ready" || cell.state === "in_progress")
                                  && !warningNotes.has(cell.job.id)
                                  && !getJobRiskSignals(cell.job.id, eventLog, auditTrail).hasAny
                                }
                                onStartCellDrag={(jobId) => beginDrag(jobId, "cell")}
                                onEndCellDrag={endDrag}
                                onOpenConflict={(jobId) => setConflictJobId(jobId)}
                                disableNavigation
                              />
                            </CellPeekPopover>
                          ) : (
                            <Cell
                              cell={cell}
                              onOpenSlot={() => openCellBooking(cell.dateIso)}
                              isDropTarget={isDropping && verdict === "valid"}
                              isDropWarning={isDropping && verdict === "warning"}
                              isDropBlocked={isDropping && verdict === "blocked"}
                              hoverHint={isHovered ? dropAnalysis?.headline ?? null : null}
                              draggingJobId={draggingJobId}
                              dragOrigin={dragOrigin}
                              onStartCellDrag={(jobId) => beginDrag(jobId, "cell")}
                              onEndCellDrag={endDrag}
                              onOpenConflict={(jobId) => setConflictJobId(jobId)}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* ── Bottom row: square map + nearest installs side panel ─── */}
      {showMap && !mapFullscreen && (
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_320px] border-b border-border/60">
          {/* Square map */}
          <div className="border-r border-border/60 flex flex-col">
            <MapHeader
              weekJobs={weekJobs}
              mapDayFilter={mapDayFilter}
              setMapDayFilter={setMapDayFilter}
              grid={grid}
              onFullscreen={() => setMapFullscreen(true)}
              fullscreen={false}
            />
            {/* True 1:1 square — width drives height. */}
            <div className="relative w-full" style={{ aspectRatio: "1 / 1", maxHeight: 620 }}>
              {weekJobs.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-[11px] text-muted-foreground bg-muted/10">
                  No installs scheduled this week. Book a ready job to plot it on the map.
                </div>
              ) : (
                <div className="absolute inset-0">
                  <PlanRouteMap
                    jobs={weekJobs}
                    issues={issues}
                    selectedJobId={focusJobId}
                    onSelectJob={setFocusJobId}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Nearest installs side panel */}
          <NearestInstallsRail
            nearestInstalls={nearestInstalls}
            onFocus={setFocusJobId}
          />
        </section>
      )}

      {/* ── Fullscreen map overlay ───────────────────────────────── */}
      {showMap && mapFullscreen && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          <MapHeader
            weekJobs={weekJobs}
            mapDayFilter={mapDayFilter}
            setMapDayFilter={setMapDayFilter}
            grid={grid}
            onFullscreen={() => setMapFullscreen(false)}
            fullscreen
          />
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_360px] min-h-0">
            <div className="relative border-r border-border/60 min-h-0">
              {weekJobs.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-[12px] text-muted-foreground">
                  No installs scheduled this week.
                </div>
              ) : (
                <PlanRouteMap
                  jobs={weekJobs}
                  issues={issues}
                  selectedJobId={focusJobId}
                  onSelectJob={setFocusJobId}
                />
              )}
            </div>
            <NearestInstallsRail
              nearestInstalls={nearestInstalls}
              onFocus={setFocusJobId}
              tall
            />
          </div>
        </div>
      )}

      <ReadyJobBookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        slotStart={bookingSlot}
        onSlotStartChange={setBookingSlot}
        preselectJobId={preselectJobId}
        scheduleReadyByJobId={scheduleReadyByJobId}
      />

      <ConflictResolutionDrawer
        open={conflictJobId !== null}
        jobId={conflictJobId}
        onClose={() => setConflictJobId(null)}
        onApplyMove={(jobId, crewName, dateIso) => bookJobToCell(jobId, crewName, dateIso, "conflict")}
      />

      {extendTarget && (
        <ExtendJobModal
          open={!!extendTarget}
          onOpenChange={(o) => { if (!o) setExtendTarget(null); }}
          job={extendTarget.job}
          crewRow={extendTarget.crewRow}
          onPreviewShiftsChange={setPreviewShifts}
        />
      )}

      <RescheduleModal
        open={!!rescheduleJob}
        onOpenChange={(o) => { if (!o) setRescheduleJob(null); }}
        job={rescheduleJob}
        openIssues={rescheduleJob ? issues.filter(i => i.jobId === rescheduleJob.id && i.status === "Open") : []}
      />

      {/* ── Confirmation Inspector ─────────────────────────────────
           Opens only after meaningful actions (rail drop, conflict
           apply, major reschedule). Cell hover/peek does NOT trigger
           it — that surface stays as the lightweight CellPeekPopover. */}
      <SchedulingConfirmInspector
        open={!!inspectorCtx}
        onOpenChange={(o) => { if (!o) setInspectorCtx(null); }}
        context={inspectorCtx}
        grid={grid}
        onExtend={(jobId) => {
          const job = jobs.find(j => j.id === jobId);
          if (!job) return;
          const crewRow = grid.rows.find(r => r.crewName ===
            (job.assignmentType === "Subcontractor" ? job.assignedSubcontractor : job.assignedCrew)) ?? null;
          setExtendTarget({ job, crewRow });
        }}
        onReschedule={(jobId) => {
          const job = jobs.find(j => j.id === jobId);
          if (job) setRescheduleJob(job);
        }}
        onReturnToQueue={(jobId) => {
          returnJobToQueue(jobId);
          setInspectorCtx(null);
        }}
        onResolveConflict={(jobId) => setConflictJobId(jobId)}
        onSwitchCrew={(jobId, crewName) => {
          // Operator-confirmed crew swap from a "Better match available"
          // suggestion. Reuses the canonical bookJobToCell path so the
          // existing event log + audit trail capture the change.
          const job = jobs.find(j => j.id === jobId);
          if (!job || !job.scheduledInstallDate) return;
          bookJobToCell(jobId, crewName, job.scheduledInstallDate, "cell");
          setInspectorCtx(null);
        }}
      />
    </TooltipProvider>
  );
}

function Legend({ dot, label, strong = false }: { dot: string; label: string; strong?: boolean }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", strong && "text-destructive font-semibold")}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      {label}
    </span>
  );
}

function DayChip({
  active, onClick, children, isToday, disabled,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  isToday?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "text-[10.5px] px-2 py-0.5 rounded-md transition-colors tabular-nums",
        active
          ? "bg-primary/10 text-primary"
          : isToday
            ? "text-primary hover:bg-primary/5"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/40",
        disabled && "opacity-40 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground",
      )}
    >
      {children}
    </button>
  );
}

function Cell({
  cell, onOpenSlot,
  isDropTarget = false, isDropWarning = false, isDropBlocked = false,
  hoverHint = null,
  draggingJobId = null, dragOrigin = null,
  onStartCellDrag, onEndCellDrag, onOpenConflict,
  disableNavigation = false, isPreviewExtension = false,
  warningNote = null,
  isClean = false,
}: {
  cell: CapacityCell;
  onOpenSlot: () => void;
  isDropTarget?: boolean;
  isDropWarning?: boolean;
  isDropBlocked?: boolean;
  hoverHint?: string | null;
  draggingJobId?: string | null;
  dragOrigin?: "rail" | "cell" | null;
  onStartCellDrag?: (jobId: string) => void;
  onEndCellDrag?: () => void;
  onOpenConflict?: (jobId: string) => void;
  /** When wrapped by CellPeekPopover, suppress the inline Link overlay so clicks open the popover. */
  disableNavigation?: boolean;
  /** Highlight this cell as part of the live Extend preview span. */
  isPreviewExtension?: boolean;
  /** Persisted "booked with warning" reason — surfaces inline so the justification is never lost. */
  warningNote?: string | null;
  /** Booked install with no warnings, no conflict, and no upstream risk signals — paint a clearer green hue. */
  isClean?: boolean;
}) {
  if (cell.state === "open") {
    // While dragging, paint every open cell with its precomputed verdict so
    // viable destinations stand out across the entire grid at a glance.
    const containerCls = cn(
      "w-full h-[58px] rounded border transition-all text-[10px] flex items-center justify-center px-1 text-center leading-tight",
      isDropTarget   ? "border-success/70 bg-success/15 text-success font-medium ring-1 ring-success/40" :
      isDropWarning  ? "border-warning/70 bg-warning/15 text-warning font-medium ring-1 ring-warning/30" :
      isDropBlocked  ? "border-destructive/40 bg-destructive/[0.04] text-destructive/60 cursor-not-allowed opacity-50" :
                       "border-dashed border-border/50 hover:border-primary/60 hover:bg-primary/5 text-muted-foreground/50 hover:text-primary",
    );
    // Microlabel logic: when hovered we show the full hint; otherwise a short
    // glance-label so all 8×7 cells stay scannable at once.
    const label =
      isDropTarget  ? (hoverHint || "✓ open") :
      isDropWarning ? (hoverHint || "⚠ review") :
      isDropBlocked ? (hoverHint || "blocked") :
                      "+";
    return (
      <button
        type="button"
        onClick={onOpenSlot}
        className={containerCls}
        aria-label="Book this slot"
      >
        <span className="truncate">{label}</span>
      </button>
    );
  }

  // Conflict gets a markedly stronger visual tier than warning/tentative —
  // operators must read "hard conflict" without hovering. Solid destructive
  // surface + double-ring + corner badge differentiates it from the soft
  // warning tint used for tentative/booked-with-warning.
  const stateStyles: Record<string, string> = {
    ready: "bg-success/10 border-success/30 text-foreground",
    tentative: "bg-warning/10 border-warning/30 text-foreground",
    in_progress: "bg-info/10 border-info/30 text-foreground",
    completed: "bg-muted/40 border-border text-muted-foreground",
    conflict: "bg-destructive/20 border-destructive border-2 text-foreground ring-2 ring-destructive/40 shadow-sm shadow-destructive/20",
  };

  const job = cell.job!;
  const isThisCellDragging = draggingJobId === job.id && dragOrigin === "cell";
  const hasWarning = !!warningNote && cell.state !== "conflict";
  const isConflict = cell.state === "conflict";
  const totalShifts = cell.totalShifts ?? 1;
  const shiftIndex = cell.shiftIndex ?? 1;
  const isMultiShift = totalShifts > 1;
  const isSpanStart = isMultiShift && shiftIndex === 1;
  const isSpanEnd = isMultiShift && shiftIndex === totalShifts;
  const isSpanMiddle = isMultiShift && shiftIndex > 1 && shiftIndex < totalShifts;
  // Already-booked cells render their own draggable wrapper so cell-to-cell
  // moves work. The whole cell is also the popover trigger (asChild) — click
  // opens the inspector, drag moves the booking. Cursor signals "click to
  // inspect" by default; a visible grip on hover signals drag-to-move.
  const canDrag = cell.state !== "completed" && cell.state !== "in_progress" && !cell.isContinuation;
  const fitTier =
    cell.fitScore == null ? null :
    cell.fitScore >= 85 ? { label: "strong", cls: "text-success" } :
    cell.fitScore >= 70 ? { label: "good",   cls: "text-foreground/80" } :
    cell.fitScore >= 50 ? { label: "marginal", cls: "text-warning" } :
                          { label: "poor",   cls: "text-destructive" };
  // Click vs drag: cell body is NOT draggable — clicks always reach the
  // popover trigger. The grip handle in the top-right is the sole drag
  // source. Eliminates the click-vs-drag ambiguity that was suppressing
  // popover access on accidental micro-movements.
  const cellInner = (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Inspect ${job.customerName} — click to open, drag the grip to move`}
      className={cn(
        "group/cell block h-[58px] rounded border px-2 py-1.5 transition-all overflow-hidden relative cursor-pointer hover:ring-1 hover:ring-primary/40 hover:shadow-sm",
        stateStyles[cell.state],
        isDropBlocked && "ring-1 ring-destructive/40",
        isThisCellDragging && "opacity-40",
        isPreviewExtension && "ring-2 ring-primary/60 bg-primary/10 border-primary/40",
        isMultiShift && "shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.08)]",
        isSpanStart && "-mr-1 rounded-r-none border-r-0",
        isSpanMiddle && "-mx-1 rounded-none border-l-0 border-r-0",
        isSpanEnd && "-ml-1 rounded-l-none border-l-0",
        // Subtle persistent left-bar marks "booked with warning" so the
        // justification is visible at a glance without hovering.
        hasWarning && !cell.isContinuation && "border-l-2 border-l-warning",
        // Clean booked install — no warnings, no conflict, no upstream risk.
        // Stronger green tint + left accent so healthy jobs are scannable
        // at a glance against the neutral grid.
        isClean && !hasWarning && !isConflict && !cell.isContinuation && "bg-success/15 border-success/40 border-l-[3px] border-l-success",
      )}
    >
      {!disableNavigation && (
        <Link
          to={`/jobs/${job.id}`}
          draggable={false}
          className="absolute inset-0 z-0"
          aria-label={`Open ${job.customerName}`}
        />
      )}
      {/* Dedicated drag handle — the ONLY draggable surface on a booked cell.
          Always-visible at low opacity so the affordance is discoverable
          (was hover-only). Tooltip explicitly states the drop-on-rail path. */}
      {disableNavigation && canDrag && (
        <div
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            e.dataTransfer.setData(DRAG_MIME, job.id);
            e.dataTransfer.effectAllowed = "move";
            onStartCellDrag?.(job.id);
          }}
          onDragEnd={() => onEndCellDrag?.()}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute top-0 right-0 h-6 w-6 z-30 flex items-center justify-center cursor-grab active:cursor-grabbing text-muted-foreground/55 hover:text-foreground hover:bg-muted/40 rounded-bl"
          role="button"
          aria-label={`Drag ${job.customerName} to move`}
          title="Drag to move · drop on Ready rail to return to queue"
        >
          <GripVertical className="h-3 w-3" aria-hidden />
        </div>
      )}
      {/* Hard-conflict corner badge — readable without hover. */}
      {isConflict && !cell.isContinuation && (
        <div className="absolute top-0 left-0 z-20 px-1 py-[1px] bg-destructive text-destructive-foreground text-[8.5px] font-bold uppercase tracking-wider rounded-br pointer-events-none">
          Conflict
        </div>
      )}
      {cell.isContinuation ? (
        <div className="relative z-10 pointer-events-none h-full flex items-center justify-center">
          <span className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground/70">
            day {shiftIndex}/{totalShifts}
          </span>
        </div>
      ) : (
        <div className={cn("relative z-10 pointer-events-none", isConflict && "pt-2")}>
          <p className="text-[10.5px] font-medium truncate leading-tight pr-3">
            {getJobDisplayRef(job)} · {job.customerName}
          </p>
          <p className="text-[9.5px] truncate opacity-70 mt-0.5 leading-tight">
            {job.address.city}
          </p>
          <div className="flex items-center justify-between mt-1 gap-1">
            {isConflict ? (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenConflict?.(job.id); }}
                className="inline-flex items-center gap-0.5 text-[9px] text-destructive font-bold hover:underline pointer-events-auto"
                aria-label="Resolve conflict"
              >
                <AlertTriangle className="h-2.5 w-2.5" />
                {cell.conflictCount} x fix now
              </button>
            ) : hasWarning ? (
              <span
                className="inline-flex items-center gap-0.5 text-[9px] text-warning font-medium truncate"
                title={warningNote ?? undefined}
              >
                <AlertTriangle className="h-2.5 w-2.5 shrink-0" />
                warned
              </span>
            ) : isMultiShift ? (
              <span className="text-[9px] opacity-70">
                {totalShifts} day install
              </span>
            ) : (
              <span className="text-[9px] opacity-60">
                {cell.state === "tentative" ? "tentative" : ""}
              </span>
            )}
            {fitTier && (
              <span className={cn("text-[9px] tabular-nums opacity-90 shrink-0", fitTier.cls)}>
                {cell.fitScore} {fitTier.label}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // When wrapped by CellPeekPopover (disableNavigation), skip the Tooltip
  // wrapper so PopoverTrigger asChild receives a single forwardable DOM node.
  if (disableNavigation) return cellInner;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{cellInner}</TooltipTrigger>
      <TooltipContent side="top" className="text-xs max-w-[240px]">
        <p className="font-medium">{getJobDisplayRef(job)} · {job.customerName}</p>
        <p className="text-muted-foreground">{job.projectType} · {job.address.city}</p>
        {cell.blockerLabel && (
          <p className="text-warning mt-1">⚠ {cell.blockerLabel}</p>
        )}
        {cell.fitScore != null && (
          <p className="text-muted-foreground mt-1">Crew fit: {cell.fitScore}/100</p>
        )}
        <p className="mt-1 inline-flex items-center gap-1 text-primary">
          Open job <ArrowRight className="h-2.5 w-2.5" />
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

// ── Map header (shared by inline + fullscreen) ──────────────────
function MapHeader({
  weekJobs, mapDayFilter, setMapDayFilter, grid, onFullscreen, fullscreen,
}: {
  weekJobs: { id: string }[];
  mapDayFilter: string | null;
  setMapDayFilter: (v: string | null | ((p: string | null) => string | null)) => void;
  grid: CapacityGrid;
  onFullscreen: () => void;
  fullscreen: boolean;
}) {
  return (
    <header className="px-4 py-3 border-b border-border/60 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
          Route map {fullscreen && <span className="text-primary normal-case tracking-normal ml-1">· Fullscreen</span>}
        </p>
        <p className="text-[11px] text-muted-foreground/80 mt-1">
          {weekJobs.length === 0
            ? "No installs scheduled — book a job to see it here."
            : `${weekJobs.length} install${weekJobs.length === 1 ? "" : "s"} ${
                mapDayFilter
                  ? `on ${format(parseISO(mapDayFilter), "EEE MMM d")}`
                  : "this week"
              }`}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          <DayChip active={mapDayFilter === null} onClick={() => setMapDayFilter(null)}>All</DayChip>
          {grid.days.map(d => {
            const count = grid.rows.reduce((s, r) => {
              const cell = r.cells.find(c => c.dateIso === d.iso);
              return s + (cell?.job ? 1 : 0);
            }, 0);
            return (
              <DayChip
                key={d.iso}
                active={mapDayFilter === d.iso}
                onClick={() => setMapDayFilter(prev => prev === d.iso ? null : d.iso)}
                isToday={d.isToday}
                disabled={count === 0}
              >
                {d.label.split(" ")[0]}
                {count > 0 && (
                  <span className="ml-1 text-[9px] opacity-70 tabular-nums">{count}</span>
                )}
              </DayChip>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onFullscreen}
          className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 text-muted-foreground hover:text-foreground transition-colors border-l border-border/60 ml-1 pl-2"
          aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {fullscreen
            ? <><X className="h-3 w-3" /> Exit</>
            : <><Maximize2 className="h-3 w-3" /> Fullscreen</>}
          <span className="opacity-60 hidden md:inline">F</span>
        </button>
      </div>
    </header>
  );
}

// ── Nearest installs side rail ───────────────────────────────────
function NearestInstallsRail({
  nearestInstalls, onFocus, tall = false,
}: {
  nearestInstalls: { job: { id: string; jobNumber?: unknown; customerName: string; address: { city: string }; projectType: string }; miles: number | null; isAnchor: boolean }[];
  onFocus: (id: string) => void;
  tall?: boolean;
}) {
  return (
    <aside className="flex flex-col min-h-0">
      <header className="px-4 py-3 border-b border-border/60">
        <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
          Nearest installs
        </p>
        <p className="text-[11px] text-muted-foreground/80 mt-1">
          {nearestInstalls.length === 0
            ? "Nothing on the map yet"
            : "Sorted from anchor — click to refocus"}
        </p>
      </header>
      <div className={cn("overflow-y-auto flex-1", tall ? "" : "max-h-[560px]")}>
        {nearestInstalls.length === 0 && (
          <div className="px-4 py-10 text-center text-[11px] text-muted-foreground">
            Book an install to populate the proximity list.
          </div>
        )}
        {nearestInstalls.map(({ job, miles, isAnchor }) => (
          <button
            key={job.id}
            type="button"
            onClick={() => onFocus(job.id)}
            className={cn(
              "w-full text-left px-4 py-2.5 border-b border-border/40 hover:bg-muted/30 transition-colors group",
              isAnchor && "bg-primary/[0.04]",
            )}
          >
            <div className="flex items-start gap-2">
              <MapPin className={cn(
                "h-3 w-3 mt-1 shrink-0",
                isAnchor ? "text-primary" : "text-muted-foreground/60",
              )} />
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-foreground truncate">
                  {getJobDisplayRef(job)} · {job.customerName}
                </p>
                <p className="text-[10.5px] text-muted-foreground truncate mt-0.5">
                  {job.address.city} · {job.projectType}
                </p>
              </div>
              <span className="text-[10px] text-muted-foreground tabular-nums shrink-0 mt-1">
                {isAnchor ? "anchor" : miles == null ? "—" : `${miles.toFixed(1)} mi`}
              </span>
            </div>
          </button>
        ))}
      </div>
      {nearestInstalls.length > 0 && (
        <div className="px-4 py-2.5 border-t border-border/60 text-[10.5px] text-muted-foreground">
          Tip: click a row to anchor proximity from that install.
        </div>
      )}
    </aside>
  );
}
function getStageGuardMessage(error: unknown, fallback: string): string {
  const msg = error instanceof Error ? error.message : String(error ?? "");
  if (msg.includes("Install stage blocked:")) return msg.replace("Install stage blocked:", "").trim();
  return fallback;
}
